import SegmentedControl from '../ds/SegmentedControl';

interface PermissionRowProps {
  label: string;
  description: string;
  currentValue: string;
  isDefault: boolean;
  readOnly?: boolean;
  permissionType: 'readOnly' | 'readWrite' | 'noneReadWrite';
  section: 'main' | 'settings' | 'actions';
  keyName: string;
  onPermissionChange: (section: 'main' | 'settings' | 'actions', key: string, value: 'None' | 'Read' | 'Write') => void;
}

/** A word, for the rows that are showing rather than asking. */
const Fixed = ({ children }: { children: React.ReactNode }) => (
  <span className="font-courrier text-ds-105 uppercase tracking-[0.08em] text-navy-300 whitespace-nowrap">
    {children}
  </span>
);

const PermissionRow = ({
  label,
  description,
  currentValue,
  isDefault,
  readOnly = false,
  permissionType,
  section,
  keyName,
  onPermissionChange,
}: PermissionRowProps) => {
  // Picking one of two or three is what the segmented control is for. These
  // were radio groups — three loose circles per row, ten rows deep, on a panel
  // where every other choice of one-from-N is a pill rail.
  const renderControl = () => {
    if (isDefault || readOnly || permissionType === 'readOnly') {
      return <Fixed>{isDefault ? currentValue : 'Read'}</Fixed>;
    }

    const values: ('None' | 'Read' | 'Write')[] = permissionType === 'readWrite'
      ? ['Read', 'Write']
      : ['None', 'Read', 'Write'];

    return (
      <SegmentedControl
        ariaLabel={`${label} permission`}
        segments={values.map(value => ({ value, label: value }))}
        value={(values as string[]).includes(currentValue) ? currentValue as 'None' | 'Read' | 'Write' : values[0]}
        onChange={(value) => onPermissionChange(section, keyName, value)}
      />
    );
  };

  return (
    <div className="flex flex-col gap-2.5 px-3.5 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="min-w-0 sm:pr-2">
        <div className="text-ds-135 font-medium text-white">{label}</div>
        <div className="text-ds-115 leading-[1.5] text-navy-300">{description}</div>
      </div>
      <div className="flex-none">{renderControl()}</div>
    </div>
  );
};

export default PermissionRow;
